import { isOpenAiAdsSuppressed, OPENAI_ADS_PIXEL_ID } from '@/constants/openai-ads';

import { createConversionEventId, getObref, getOppref } from './openai-ads-attribution';
import { stripSensitiveQuery } from './strip-sensitive-query';

/**
 * Reports a conversion to OpenAI Ads through both transports at once: the browser pixel and, via our
 * own API route, the Conversions API.
 *
 * Both, not one: OpenAI treats the server signal as the reliable one (it survives ad blockers and a
 * tab closed mid-redirect) while the pixel is what carries the browser-side `__obref` match. Sending
 * the same `event_id`/`id` in both makes the pair a single conversion — OpenAI keeps whichever arrives
 * first and drops the other, so there is no double counting. See `docs/openai-ads-conversions.md`.
 *
 * Every path here is best-effort and silent. A conversion that fails to report is a reporting problem;
 * a signup that fails because reporting threw would be a revenue problem, so nothing in this module is
 * allowed to reject into the caller.
 */

/** Standard OpenAI event names we emit. `trial_started` is the optimization goal; the other is context. */
export type OpenAiAdsEventType = 'trial_started' | 'registration_completed';

/** `data.type` for the event, from the fixed set the API accepts. */
const DATA_TYPE_BY_EVENT: Record<OpenAiAdsEventType, 'plan_enrollment' | 'customer_action'> = {
  // A trial IS a plan enrolment in OpenAI's vocabulary, and the docs name this pairing explicitly.
  trial_started: 'plan_enrollment',
  registration_completed: 'customer_action',
};

const CONVERSION_ENDPOINT = '/api/openai-ads-conversion';

interface OpenAiAdsConversionInput {
  type: OpenAiAdsEventType;
  /** Trial config id (`custom`), when the conversion came from a custom trial page. */
  planId?: number | string | null;
  /** Hashed on the server before it leaves our infrastructure — never sent to OpenAI in the clear. */
  email?: string | null;
}

interface OaiqQueue {
  (command: 'measure', eventName: string, eventData: Record<string, unknown>, options: Record<string, unknown>): void;
  (command: string, ...args: unknown[]): void;
}

declare global {
  interface Window {
    oaiq?: OaiqQueue;
  }
}

/**
 * Conversions already reported in this document, keyed by event type.
 *
 * `handleCreateTrial` runs once per accepted signup, but React StrictMode double-invokes effects and a
 * caller can always retry a submit whose response it already got. Requirement 7 of the briefing is that
 * one trial produces exactly one conversion, and a new `event_id` per call would defeat the dedup that
 * OpenAI does for us — so the guard is here, before the id is generated.
 */
const reportedEvents = new Set<OpenAiAdsEventType>();

/** Resets the once-per-visit guard. Tests only: nothing in the app re-reports a conversion on purpose. */
export const resetOpenAiAdsConversionsForTests = (): void => reportedEvents.clear();

const measureWithPixel = (type: OpenAiAdsEventType, eventId: string, planId: string | undefined): void => {
  // `oaiq` is the queue stub the snippet installs synchronously, so calls made before oaiq.min.js
  // finishes downloading are replayed on load. Its absence means the pixel never mounted at all —
  // no pixel id, staging — and there is nothing to queue into.
  if (typeof window === 'undefined' || typeof window.oaiq !== 'function') return;

  const eventData: Record<string, unknown> = { type: DATA_TYPE_BY_EVENT[type] };
  if (planId) eventData.plan_id = planId;

  window.oaiq('measure', type, eventData, { event_id: eventId });
};

const sendToConversionsApi = (type: OpenAiAdsEventType, eventId: string, input: OpenAiAdsConversionInput): void => {
  const body = JSON.stringify({
    eventId,
    type,
    oppref: getOppref(),
    obref: getObref(),
    planId: input.planId ?? null,
    email: input.email ?? null,
    // The Discord callback reports `trial_started` before it leaves, while its URL still holds the OAuth
    // `code`/`state` — those must not reach OpenAI as `source_url`. The route strips them again.
    sourceUrl: stripSensitiveQuery(window.location.href),
  });

  // `keepalive` because the LP that reports a trial usually navigates away in the same tick (success
  // screen, Discord invite, desktop app handoff). Without it the request dies with the document and
  // the server copy — the reliable half of the pair — is the one we lose.
  fetch(CONVERSION_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    // Offline, blocked, or our own route down. The pixel copy may still land; either way the signup
    // already succeeded and the visitor must not see anything about it.
  });
};

/**
 * Fires one conversion through both transports. No-op when the pixel is not configured, on staging, or
 * when this event type was already reported in this document.
 */
export const reportOpenAiAdsConversion = (input: OpenAiAdsConversionInput): void => {
  if (typeof window === 'undefined') return;
  if (!OPENAI_ADS_PIXEL_ID) return;
  // A staging QA run must not reach the ad account at all, and this is the one check that does not
  // depend on the visitor.
  if (isOpenAiAdsSuppressed()) return;
  if (reportedEvents.has(input.type)) return;

  reportedEvents.add(input.type);

  const eventId = createConversionEventId();
  const planId = input.planId === null || input.planId === undefined ? undefined : String(input.planId);

  try {
    measureWithPixel(input.type, eventId, planId);
    sendToConversionsApi(input.type, eventId, input);
  } catch {
    // Reporting is never worth an exception on the signup path.
  }
};
